/**
 * Shared helpers for spending-phase category totals and itemization (#1538).
 * Used by SpendingPhasesTable, SpendingPhaseEditDrawer, and Step5Spending.mobile.
 */

import type { ExpenseLineItem, PersonData, PersonDeceasedAge, SpendingPhase } from '../types/wizard';
import { parseLocalDate } from '../types/wizard';
import { planYearToCalendar } from './formatters';

export type SpendingCategoryKey = 'livingExpenses' | 'travelLeisure' | 'otherDiscretionary';

export const CATEGORY_ORDER: readonly SpendingCategoryKey[] = ['livingExpenses', 'travelLeisure', 'otherDiscretionary'];

export const CATEGORY_META: Record<SpendingCategoryKey, { label: string; hint: string; itemsField: 'livingExpensesItems' | 'travelLeisureItems' | 'otherDiscretionaryItems' }> = {
  livingExpenses: { label: 'General Living', hint: 'Food, utilities, insurance, home maintenance', itemsField: 'livingExpensesItems' },
  travelLeisure: { label: 'Travel & Leisure', hint: 'Holidays, trips, entertainment', itemsField: 'travelLeisureItems' },
  otherDiscretionary: { label: 'Other', hint: 'Hobbies, gifts, personal spending', itemsField: 'otherDiscretionaryItems' },
};

export function getCategoryItems(phase: SpendingPhase | undefined, key: SpendingCategoryKey): ExpenseLineItem[] | undefined {
  if (!phase) return undefined;
  return phase[CATEGORY_META[key].itemsField];
}

export function isCategoryItemized(phase: SpendingPhase | undefined, key: SpendingCategoryKey): boolean {
  return getCategoryItems(phase, key) != null;
}

export function sumItems(items: ExpenseLineItem[] | undefined): number {
  if (!items) return 0;
  return items.reduce((sum, item) => sum + (Number(item.annualAmount) || 0), 0);
}

/** A category's total is the sum of its items when itemized, otherwise the flat field. */
export function getCategoryTotal(phase: SpendingPhase | undefined, key: SpendingCategoryKey): number {
  if (!phase) return 0;
  const items = getCategoryItems(phase, key);
  return items ? sumItems(items) : (Number(phase[key]) || 0);
}

export function getPhaseTotal(phase: SpendingPhase | undefined): number {
  if (!phase) return 0;
  return CATEGORY_ORDER.reduce((sum, key) => sum + getCategoryTotal(phase, key), 0);
}

/** Whether a phase already has non-zero spending entered anywhere — used to gate the copy-from overwrite warning. */
export function phaseHasSpendingData(phase: SpendingPhase | undefined): boolean {
  if (!phase) return false;
  return CATEGORY_ORDER.some((key) => {
    const items = getCategoryItems(phase, key);
    return items ? items.length > 0 : (Number(phase[key]) || 0) > 0;
  });
}

/** Default 3-phase starting point for a new plan — shared by desktop and mobile Step 6. */
export function createDefaultSpendingPhases(): SpendingPhase[] {
  return [
    {
      phaseId: crypto.randomUUID(),
      label: 'Early Retirement - Active Years',
      startYear: 1,
      endYear: 10,
      livingExpenses: 35000,
      travelLeisure: 15000,
      otherDiscretionary: 5000,
      inflationLinked: true,
    },
    {
      phaseId: crypto.randomUUID(),
      label: 'Mid Retirement',
      startYear: 11,
      endYear: 20,
      livingExpenses: 30000,
      travelLeisure: 10000,
      otherDiscretionary: 3000,
      inflationLinked: true,
    },
    {
      phaseId: crypto.randomUUID(),
      label: 'Later Years',
      startYear: 21,
      endYear: undefined,
      livingExpenses: 25000,
      travelLeisure: 5000,
      otherDiscretionary: 2000,
      inflationLinked: true,
    },
  ];
}

/**
 * Parses a "£1,234.50"-ish string into a number, stripping currency symbols,
 * thousands separators, and surrounding whitespace. Returns null (not a
 * silent 0) when the text isn't a recognizable number, so callers can tell
 * "blank/header cell" apart from "genuinely zero".
 */
export function parseCurrencyLike(text: string | undefined): number | null {
  if (!text) return null;
  const cleaned = text.replace(/[£$€,\s]/g, '');
  if (cleaned === '') return null;
  const num = Number.parseFloat(cleaned);
  return Number.isNaN(num) ? null : num;
}

/**
 * True when pasted clipboard text looks like a multi-cell spreadsheet
 * selection (tab-separated columns and/or multiple rows) rather than a
 * single plain-text value — the signal used to intercept a paste instead of
 * letting it land as normal text in whichever field it was pasted into.
 */
export function looksLikeTabularPaste(text: string): boolean {
  const trimmed = text.replace(/\r\n/g, '\n').trim();
  return trimmed.includes('\t') || trimmed.includes('\n');
}

/**
 * Parses clipboard text copied from a two-column Excel/Sheets selection
 * (Description, Annual Amount) into line items. Assumes description comes
 * first. Drops a leading header row (detected by its second cell not
 * parsing as a number) and blank rows; a row whose amount cell doesn't
 * parse is kept with amount 0 rather than silently dropped, so the user's
 * pasted description isn't lost — they can fix the amount by hand.
 */
export function parseClipboardLineItems(text: string): Array<{ description: string; annualAmount: number }> {
  const rows = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((row) => row.split('\t').map((cell) => cell.trim()))
    .filter((cells) => cells.some((cell) => cell !== ''));

  if (rows.length > 0 && parseCurrencyLike(rows[0][1]) == null) {
    rows.shift(); // likely a header row, e.g. "Description  Amount"
  }

  return rows.map((cells) => ({
    description: cells[0] || '',
    annualAmount: parseCurrencyLike(cells[1]) ?? 0,
  }));
}

/**
 * Projects a "today's money" amount forward to what it will be in future
 * (nominal, i.e. inflated) terms by the time a given phase starts — the
 * same anchor-to-plan-Year-1 compounding the engine itself uses
 * (`calculatePhaseSpending` in the server's spendingCalculator.ts), so this
 * mirrors the formula the engine will actually compute (unrounded — the
 * engine rounds to the penny, invisible after currency formatting), not a
 * separate assumption. Year 1 has 0 years of compounding (it IS today).
 */
export function projectAmountToPhaseStart(amount: number, inflationAnnualPct: number, startYear: number): number {
  const years = Math.max(0, startYear - 1);
  return amount * Math.pow(1 + inflationAnnualPct / 100, years);
}

/**
 * "2026–2030"-style calendar-year range for a phase's plan years, or just
 * "2051" (start year only) for an open-ended phase (no endYear, or a blank
 * RHF number field left as NaN) — callers already render the word "onwards"
 * next to the plan-year range, so this doesn't repeat it. Null when
 * planAsOfDate isn't known yet.
 */
export function formatPhaseCalendarYearRange(phase: SpendingPhase, planAsOfDate: string | undefined): string | null {
  if (!planAsOfDate) return null;
  const startCalYear = planYearToCalendar(phase.startYear, planAsOfDate);
  if (!Number.isFinite(phase.endYear)) return `${startCalYear}`;
  const endCalYear = planYearToCalendar(phase.endYear as number, planAsOfDate);
  return startCalYear === endCalYear ? `${startCalYear}` : `${startCalYear}–${endCalYear}`;
}

/** A person's age in a given plan year, from their date of birth and the plan's as-of date. Null if either date is unparseable. */
function ageInPlanYear(dateOfBirth: string, planAsOfDate: string, planYear: number): number | null {
  const dob = parseLocalDate(dateOfBirth);
  const asOf = parseLocalDate(planAsOfDate);
  if (!dob || !asOf) return null;

  let baseAge = asOf.getFullYear() - dob.getFullYear();
  const monthDiff = asOf.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < dob.getDate())) {
    baseAge--;
  }
  return baseAge + (planYear - 1);
}

/**
 * "Bob 55–60, Caroline 53–58"-style age range for a phase, one segment per
 * person still alive at the phase's start (per personDeceasedAges, when
 * known — a person who died before this phase starts is silently omitted
 * rather than shown with a placeholder). An open-ended phase (no endYear)
 * runs to the plan's projection horizon (projectionYears, Step 8) — the
 * household's assumed life-expectancy cutoff — shown the same as a closed
 * range; falls back to "55+" only when projectionYears isn't known yet
 * (e.g. mid-wizard, before Step 8 is filled in). Returns null when there's
 * nothing to show (no persons/plan date).
 */
export function formatPhaseAgeRange(
  phase: SpendingPhase,
  persons: readonly PersonData[] | undefined,
  planAsOfDate: string | undefined,
  personDeceasedAges: readonly PersonDeceasedAge[] | undefined,
  projectionYears: number | undefined,
): string | null {
  if (!persons?.length || !planAsOfDate) return null;

  // React Hook Form's valueAsNumber leaves an empty "End Year" field as NaN,
  // not undefined — Number.isFinite is the established open-ended check
  // (see SpendingPhaseEditDrawer.tsx).
  const effectiveEndYear = Number.isFinite(phase.endYear)
    ? phase.endYear
    : (Number.isFinite(projectionYears) ? projectionYears : undefined);

  const parts = persons
    .map((person, index) => {
      if (!person.dateOfBirth || !person.fullName) return null;
      const startAge = ageInPlanYear(person.dateOfBirth, planAsOfDate, phase.startYear);
      if (startAge == null) return null;

      const deceasedAge = personDeceasedAges?.find((d) => d.personIndex === index)?.deceasedAge;
      if (deceasedAge != null && startAge > deceasedAge) return null; // not alive for this phase

      const firstName = person.fullName.trim().split(/\s+/)[0];
      // endAge uses endYear+1 (the age they'll turn as the next phase begins), so a
      // 5-year phase reads as a 5-year age span (e.g. "55–60") rather than 4.
      const endAge = effectiveEndYear != null ? ageInPlanYear(person.dateOfBirth, planAsOfDate, effectiveEndYear + 1) : null;
      return endAge != null ? `${firstName} ${startAge}–${endAge}` : `${firstName} ${startAge}+`;
    })
    .filter((part): part is string => part != null);

  return parts.length > 0 ? parts.join(', ') : null;
}
